import { useCallback, useEffect, useRef, useState } from "react";
import { createSseClient, type SseConnectionState } from "../lib/sseClient";
import { apiCall, UnauthorizedError } from "../lib/apiClient";

/**
 * UC-001 sync progress SSE hook (Phase 13).
 *
 * Konzumuje `/api/admin/sync/progress` event stream + agreguje per-zone state.
 * Reconnect mode přes [sseClient] s exp backoff (REQ-UI-024).
 *
 * Per REQ-UI-024 pull fallback: hook spustí `pullIntervalMs` (default 30s)
 * GET na `/api/admin/sync/state` aby UI vidělo poslední stav i v případě
 * SSE disconnect / proxy stripping path.
 */

export interface SyncProgressEvent {
  zone_id: string;
  zone_code: string;
  phase: string;
  state: string;
  progress_pct: number;
  sync_run_id: string;
  inventory_count: number;
  downloaded_count: number;
  download_failed: number;
  message: string;
  timestamp: string;
}

export interface SyncStateZoneSnapshot {
  zone_id: string;
  zone_code: string;
  billing_group_code: string;
  active: boolean;
  last_state: string;
  last_sync_at: string | null;
  duration_seconds: number | null;
  /** Kumulativní počet dokladů v DB (ne delta běhu). */
  inventory_count: number | null;
  last_sync_run_id: string | null;
  /** Kumulativní počty v DB: transakce + karty zóny. Volitelné (test fixtures
   *  je nemusí uvádět); reálné /api/admin/sync/state je posílá vždy. */
  transaction_count?: number;
  card_count?: number;
}

export interface SyncProgressSnapshot {
  /** Latest event per zone — keyed by zone_code (S0/S1/S2). */
  byZone: Record<string, SyncProgressEvent>;
  connection: SseConnectionState;
  lastError: string | null;
}

const EMPTY_SNAPSHOT: SyncProgressSnapshot = {
  byZone: {},
  connection: "connecting",
  lastError: null,
};

export interface UseSyncProgressSseOptions {
  url?: string;
  /** Pull-fallback URL (REQ-UI-024). */
  statePullUrl?: string;
  pullIntervalMs?: number;
  /** Set true v testech pro vypnutí pull fallback timeru. */
  disablePull?: boolean;
}

export function useSyncProgressSse(opts: UseSyncProgressSseOptions = {}): {
  snapshot: SyncProgressSnapshot;
  zones: SyncStateZoneSnapshot[];
  refreshState: () => Promise<void>;
} {
  const {
    url = "/api/admin/sync/progress",
    statePullUrl = "/api/admin/sync/state",
    pullIntervalMs = 30_000,
    disablePull = false,
  } = opts;

  const [snapshot, setSnapshot] = useState<SyncProgressSnapshot>(EMPTY_SNAPSHOT);
  const [zones, setZones] = useState<SyncStateZoneSnapshot[]>([]);
  const refreshRef = useRef<() => Promise<void>>(async () => {});
  const stoppedRef = useRef(false);

  // Pull-state — initial fetch + interval fallback.
  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      if (stoppedRef.current) return;
      try {
        const json = await apiCall<{ zones: SyncStateZoneSnapshot[] }>(statePullUrl);
        if (!cancelled) setZones(json.zones ?? []);
      } catch (err) {
        if (err instanceof UnauthorizedError) {
          // Global handler v apiClient zařídí redirect; další polling už nemá
          // smysl — zastavíme interval, aby se 401 nespamoval ve smyčce.
          stoppedRef.current = true;
        }
        // Ostatní chyby (network, 5xx) jen spolkneme — interval to retrynue.
      }
    };
    refreshRef.current = refresh;
    refresh();
    const id = disablePull ? null : setInterval(refresh, pullIntervalMs);
    return () => {
      cancelled = true;
      if (id) clearInterval(id);
    };
  }, [statePullUrl, pullIntervalMs, disablePull]);

  // SSE event stream — per-zone aggregation.
  useEffect(() => {
    const handle = createSseClient({
      url,
      onEvent: (eventName, data) => {
        if (eventName !== "progress" && eventName !== "message") return;
        const event = data as SyncProgressEvent;
        if (!event || !event.zone_code) return;
        setSnapshot((prev) => ({
          ...prev,
          byZone: { ...prev.byZone, [event.zone_code]: event },
          lastError: null,
        }));
      },
      onConnectionStateChange: (connState) => {
        setSnapshot((prev) => ({ ...prev, connection: connState }));
      },
      onError: () => {
        setSnapshot((prev) => ({ ...prev, lastError: "sse_error" }));
        // Trigger pull fallback (REQ-UI-024 disconnect handling).
        void refreshRef.current?.();
      },
    });
    return () => handle.close();
  }, [url]);

  // Stabilní identita pro consumer useEffect deps — bez useCallback by každý
  // re-render hooku (typicky SSE event) vytvořil novou referenci, což trigeruje
  // useEffecty v consumeru spam-stylem.
  const refreshState = useCallback(() => refreshRef.current(), []);
  return { snapshot, zones, refreshState };
}
